import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Github, Shield, Database, Cloud, Lock, Users, FileText } from 'lucide-react';
import './SoulProject.css';

function SoulProject() {
    const navigate = useNavigate();

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            icon: <Lock size={32} />,
            title: "Secure Storage",
            desc: "End-to-end encrypted storage for digital assets using AES-256 encryption"
        },
        {
            icon: <Cloud size={32} />,
            title: "Decentralized Architecture",
            desc: "Built on Blockchain and IPFS for maximum security and permanence"
        },
        {
            icon: <Users size={32} />,
            title: "Beneficiary Management",
            desc: "Automated transfer of digital assets to designated beneficiaries"
        },
        {
            icon: <Shield size={32} />,
            title: "Smart Contracts",
            desc: "Blockchain-based automation for secure and trustless execution"
        },
        {
            icon: <Database size={32} />,
            title: "Multi-Format Support",
            desc: "Store documents, credentials, media files, and cryptocurrency information"
        },
        {
            icon: <FileText size={32} />,
            title: "Digital Will",
            desc: "Create comprehensive digital legacy plans with custom instructions"
        }
    ];

    const techStack = [
        { category: "Mobile", items: ["React Native", "Expo", "AsyncStorage"] },
        { category: "Backend", items: ["Node.js", "Express.js", "MongoDB"] },
        { category: "Blockchain", items: ["Blockchain", "IPFS", "Smart Contracts"] },
        { category: "Security", items: ["AES-256", "JWT", "Bcrypt"] }
    ];

    return (
        <div className="soul-project">
            {/* Header */}
            <header className="project-header">
                <button onClick={() => navigate('/#projects')} className="back-btn">
                    <ArrowLeft size={20} /> Back to Portfolio
                </button>

                <div className="project-hero">
                    <span className="project-tag">Flagship Project</span>
                    <h1>S.O.U.L</h1>
                    <h2>Secure Online Unified Locker</h2>
                    <p className="tagline">
                        A decentralized digital legacy planning platform that enables users to securely
                        store digital assets and automate their transfer to beneficiaries upon death.
                    </p>
                </div>
            </header>

            {/* Overview Section */}
            <section className="section">
                <h3>Project Overview</h3>
                <div className="overview-content">
                    <p>
                        S.O.U.L (Secure Online Unified Locker) is a revolutionary digital legacy planning
                        platform that addresses the growing need for secure digital asset management and
                        inheritance in the modern age. With the increasing digitization of our lives,
                        important documents, credentials, and digital assets often become inaccessible
                        to loved ones after death.
                    </p>
                    <p>
                        This platform leverages cutting-edge blockchain technology and decentralized
                        storage (IPFS) to create a secure, tamper-proof system for storing and
                        transferring digital assets. The use of smart contracts ensures that the
                        transfer process is automated, trustless, and executed exactly as intended
                        by the user.
                    </p>
                </div>
            </section>

            {/* Features Section */}
            <section className="section">
                <h3>Key Features</h3>
                <div className="features-grid">
                    {features.map((feature, index) => (
                        <div key={index} className="feature-card">
                            <div className="feature-icon">{feature.icon}</div>
                            <h4>{feature.title}</h4>
                            <p>{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Tech Stack Section */}
            <section className="section">
                <h3>Technology Stack</h3>
                <div className="tech-grid">
                    {techStack.map((tech, index) => (
                        <div key={index} className="tech-category">
                            <h4>{tech.category}</h4>
                            <div className="tech-items">
                                {tech.items.map((item, i) => (
                                    <span key={i} className="tech-badge">{item}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Problem & Solution */}
            <section className="section">
                <h3>Problem & Solution</h3>
                <div className="problem-solution">
                    <div className="ps-card problem">
                        <h4>The Problem</h4>
                        <ul>
                            <li>Digital assets become inaccessible after death</li>
                            <li>No secure way to pass on passwords and credentials</li>
                            <li>Cryptocurrency wallets lost forever</li>
                            <li>Important documents scattered across platforms</li>
                            <li>Lack of trust in centralized solutions</li>
                        </ul>
                    </div>
                    <div className="ps-card solution">
                        <h4>Our Solution</h4>
                        <ul>
                            <li>Decentralized storage ensures permanence</li>
                            <li>End-to-end encryption protects sensitive data</li>
                            <li>Smart contracts automate inheritance</li>
                            <li>Unified platform for all digital assets</li>
                            <li>Blockchain ensures transparency and trust</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="section">
                <h3>How It Works</h3>
                <div className="workflow">
                    <div className="workflow-step">
                        <div className="step-number">1</div>
                        <h4>Create Account</h4>
                        <p>Sign up and set up your secure digital locker with multi-factor authentication</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">2</div>
                        <h4>Store Assets</h4>
                        <p>Upload documents, credentials, and digital asset information with AES-256 encryption</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">3</div>
                        <h4>Add Beneficiaries</h4>
                        <p>Designate trusted beneficiaries and set custom access rules and conditions</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">4</div>
                        <h4>Automated Transfer</h4>
                        <p>Smart contracts automatically transfer assets to beneficiaries when conditions are met</p>
                    </div>
                </div>
            </section>

            {/* Project Links */}
            <section className="section">
                <div className="project-links">
                    <a href="https://github.com/ANJOEJAMES" target="_blank" rel="noreferrer" className="project-btn">
                        <Github size={20} /> View on GitHub
                    </a>
                    <button onClick={() => navigate('/#projects')} className="project-btn secondary">
                        <ArrowLeft size={20} /> Back to Portfolio
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="project-footer">
                <p>© {new Date().getFullYear()} Anjoe James. S.O.U.L Project.</p>
            </footer>
        </div>
    );
}

export default SoulProject;
